import React, { useCallback, useEffect, useState } from "react";
import {
    listOwnWarehouseFulfillmentIssues,
    resolveOwnWarehouseFulfillmentIssue,
} from "../api/ownWarehouseApi";

const issueLabel = (value) => ({
    handover_without_manifest: "Передача НП без подтверждённого состава",
    manifest_confirmation_required: "Посылка НП требует подтверждения фактического состава",
    manifest_not_confirmed: "Сборка подтверждена, но состав не сошёлся с резервами (наблюдение)",
    manifest_reservation_mismatch: "Состав не совпадает с резервами",
    manifest_allocation_missing: "В подтверждённом составе нет распределения по резервам",
    manifest_allocation_invalid: "Распределение по резервам повреждено",
    manifest_empty: "Подтверждённый состав пуст",
    manifest_duplicate_reservation: "Один резерв привязан несколько раз",
    handover_manifest_conflict: "Конфликт проведённого состава",
    crm_changed_after_handover: "CRM изменена после передачи НП",
    shipment_composition_changed: "Заказ изменён после начала сборки",
    shipment_retry_after_writeoff: "Повторная отгрузка после уже проведённого списания",
    handover_post_failed: "Не удалось провести передачу НП",
    handover_post_disabled: "Передача найдена; автоматическое списание пока выключено",
    reservation_change_after_pack: "Резерв изменён после начала сборки",
    reservation_change_after_handover: "Резерв изменён после передачи НП",
    np_tracking_exception: "Ошибка проверки ТТН в НП или отсутствуют доступы",
    np_tracking_unknown_code: "Неизвестный код статуса НП",
    np_handover_code_unverified: "Код НП не разрешён для складского списания",
    order_cancelled_after_pack: "Заказ отменён после сборки — требуется проверка",
    order_cancelled_after_handover: "Заказ отменён после передачи НП",
    unpack_shortage: "Недостача после распаковки",
}[value] || value || "Требуется сверка склада");

const UNPACKABLE_TYPES = ["order_cancelled_after_pack", "reservation_change_after_pack", "shipment_composition_changed"];
const READ_ONLY_TYPES = ["handover_post_disabled", "np_tracking_exception", "np_tracking_unknown_code", "np_handover_code_unverified"];

const needsReturn = (item) => item.issue_type === "order_cancelled_after_pack"
    && ["written_off", "mixed"].includes(item.stock_accounting_state);
const readOnly = (item) => READ_ONLY_TYPES.includes(item.issue_type) || needsReturn(item);
const canUnpack = (item) => UNPACKABLE_TYPES.includes(item.issue_type) && !item.handover_at
    && !item.posted_at && !needsReturn(item)
    && (item.issue_type !== "order_cancelled_after_pack" || item.stock_accounting_state === "reserved");

const defaultAction = (item) => {
    if (item.issue_type === "crm_changed_after_handover") return "acknowledge_post_handover";
    if (item.issue_type === "order_cancelled_after_pack") return canUnpack(item) ? "unpack" : "confirm_manifest";
    if (item.issue_type === "unpack_shortage") return "acknowledge_shortage";
    return "confirm_manifest";
};

const issueError = (error) => error?.response?.data?.detail || error?.message || "Операция не выполнена";

const OwnWarehouseFulfillmentIssuesTab = ({ warehouse }) => {
    const [items, setItems] = useState([]);
    const [health, setHealth] = useState(null);
    const [comments, setComments] = useState({});
    const [actions, setActions] = useState({});
    const [repackLines, setRepackLines] = useState({});
    const [unpackLines, setUnpackLines] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const load = useCallback(async () => {
        if (!warehouse?.id) {
            setItems([]);
            setHealth(null);
            return;
        }
        setLoading(true);
        setError("");
        try {
            const data = await listOwnWarehouseFulfillmentIssues(warehouse.id);
            const nextItems = Array.isArray(data?.items) ? data.items : [];
            setItems(nextItems);
            setHealth(data?.health || null);
            setRepackLines((current) => {
                const next = { ...current };
                nextItems.forEach((item) => {
                    if (!next[item.id]) {
                        const proposed = item.crm_qty_by_sku || item.shipment_qty_by_sku || {};
                        next[item.id] = Object.entries(proposed).map(([sku, qty]) => ({ sku, qty: Number(qty) }));
                    }
                });
                return next;
            });
            setUnpackLines((current) => {
                const next = { ...current };
                nextItems.forEach((item) => {
                    if (!next[item.id]) {
                        const packed = item.details?.packed_qty_by_sku || item.shipment_qty_by_sku || {};
                        next[item.id] = Object.entries(packed).map(([sku, qty]) => ({ sku, inBox: Number(qty), returned: Number(qty) }));
                    }
                });
                return next;
            });
            setActions((current) => {
                const next = { ...current };
                nextItems.forEach((item) => {
                    if (!next[item.id]) next[item.id] = defaultAction(item);
                });
                return next;
            });
        } catch (err) {
            setError(issueError(err));
        } finally {
            setLoading(false);
        }
    }, [warehouse?.id]);

    useEffect(() => { load(); }, [load]);

    const resolve = async (item) => {
        const comment = String(comments[item.id] || "").trim();
        if (comment.length < 3) {
            setError("Добавьте комментарий с результатом проверки и выполненным действием.");
            return;
        }
        const action = actions[item.id] || defaultAction(item);
        const lines = repackLines[item.id] || [];
        if (action === "repack" && (!lines.length || lines.some((line) => !String(line.sku || "").trim() || Number(line.qty) <= 0))) {
            setError("Для пересборки заполните каждый SKU и положительное количество.");
            return;
        }
        const unpack = unpackLines[item.id] || [];
        if (action === "unpack" && (!unpack.length || unpack.some((line) => Number(line.returned) < 0 || Number(line.returned) > Number(line.inBox)))) {
            setError("Для распаковки укажите по каждому SKU, сколько вернулось на полку (не больше, чем было в коробке).");
            return;
        }
        setBusyId(item.id);
        setError("");
        setMessage("");
        try {
            const result = await resolveOwnWarehouseFulfillmentIssue(item.id, {
                comment,
                action,
                expected_manifest_version: Number(item.manifest_version || 0),
                expected_task_version: item.task_version == null ? null : Number(item.task_version),
                ...(action === "repack" ? { lines: lines.map((line) => ({ sku: String(line.sku).trim(), qty: Number(line.qty) })) } : {}),
                ...(action === "unpack" ? { lines: unpack.map((line) => ({ sku: String(line.sku).trim(), qty: Number(line.returned), qty_returned: Number(line.returned) })) } : {}),
            });
            setMessage(result?.unpacked
                ? (result.fully_unpacked
                    ? `Посылка #${item.id} распакована полностью, резерв снят, товар снова доступен.`
                    : `Посылка #${item.id} распакована частично. Недостача: ${Object.entries(result.shortage_by_sku || {}).map(([sku, qty]) => `${sku} × ${qty}`).join(", ")} — открыта отдельная проблема.`)
                : result?.repacked
                ? `Посылка #${item.id} пересобрана. Сканирование сброшено; нужно заново проверить весь состав в приложении.`
                : result?.linked_existing
                    ? `Проблема #${item.id} закрыта: существующее списание связано с подтверждённым составом.`
                    : result?.message
                        ? result.message
                    : result?.status === "resolved"
                        ? (result?.posted ? `Проблема #${item.id} закрыта, состав проведён.` : `Проблема #${item.id} закрыта.`)
                        : `Результат действия сохранён для проблемы #${item.id}.`);
            setComments((current) => ({ ...current, [item.id]: "" }));
            await load();
        } catch (err) {
            setError(issueError(err));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="sp-section">
            <div className="sp-card sp-panel">
                <div className="sp-toolbar-head">
                    <div>
                        <h3>Склад · Требуют проверки</h3>
                        <p className="lead">Передача перевозчику и состав товара сверяются отдельно. Подтверждение состава фиксирует текущие строки отгрузки; движение остатка проводится только после подтверждённой передачи НП.</p>
                    </div>
                    <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={load} disabled={loading || !warehouse}>
                        {loading ? "Обновление…" : "Обновить"}
                    </button>
                </div>
                {error && <div className="sp-msg err">{error}</div>}
                {message && <div className="sp-msg ok">{message}</div>}
                {health ? (
                    <div className="ow-fulfillment-issue-facts" aria-label="Состояние складской сверки">
                        <span>Передач без проводки <b>{health.pending_handovers}</b>{health.oldest_handover_at ? ` · с ${new Date(health.oldest_handover_at).toLocaleString()}` : ""}</span>
                        <span>Блокирующих конфликтов <b>{health.open_conflicts}</b>{health.oldest_conflict_at ? ` · с ${new Date(health.oldest_conflict_at).toLocaleString()}` : ""}</span>
                        <span>Ошибок уведомлений <b>{health.notification_errors}</b>{health.oldest_notification_error_at ? ` · с ${new Date(health.oldest_notification_error_at).toLocaleString()}` : ""}</span>
                        <span>Ожидает себестоимость <b>{health.cost_updates_pending}</b>{health.oldest_cost_update_at ? ` · с ${new Date(health.oldest_cost_update_at).toLocaleString()}` : ""}</span>
                    </div>
                ) : null}
                {!warehouse ? <p className="sp-hint">Выберите склад.</p> : null}
                {warehouse && !loading && items.length === 0 && !error ? <p className="sp-hint">Открытых проблем нет.</p> : null}
                <div className="ow-fulfillment-issues">
                    {items.map((item) => (
                        <article className="sp-card ow-fulfillment-issue" key={item.id}>
                            <div className="sp-toolbar-head">
                                <div>
                                    <h4>{issueLabel(item.issue_type)}</h4>
                                    <div className="sp-hint">Заказ #{item.salesdrive_order_id} · ТТН {item.ttn_number || "не указана"} · отгрузка #{item.shipment_id}</div>
                                </div>
                                <span className="sp-stat warn">Открыта {new Date(item.created_at).toLocaleString()}</span>
                            </div>
                            <div className="ow-fulfillment-issue-facts">
                                <span>Склад <b>{item.warehouse_id}</b></span>
                                <span>Продажи <b>{(item.blocks_sales ?? item.blocks_sku) ? "заблокированы" : "доступны по свободному остатку"}</b></span>
                                <span>В резерве <b>{Object.entries(item.reserved_qty_by_sku || {}).map(([sku, qty]) => `${sku} × ${qty}`).join(", ") || "—"}</b></span>
                                <span>Списано <b>{Object.entries(item.written_off_qty_by_sku || {}).map(([sku, qty]) => `${sku} × ${qty}`).join(", ") || "—"}</b></span>
                                <span>Состав подтверждён <b>{item.manifest_version ? `v${item.manifest_version}` : "нет"}</b></span>
                                {item.task_version != null ? <span>Версия сборки <b>v{item.task_version}</b> · {item.task_status}</span> : null}
                                <span>Передача НП <b>{item.handover_at ? new Date(item.handover_at).toLocaleString() : "не подтверждена"}</b></span>
                                <span>Движение <b>{item.posted_at ? new Date(item.posted_at).toLocaleString() : "нет"}</b></span>
                                <span>Уведомлений <b>{item.notification_attempts || 0}</b></span>
                            </div>
                            {Object.prototype.hasOwnProperty.call(item.details || {}, "target_warehouse_id")
                                && item.details?.target_warehouse_id !== item.warehouse_id ? (
                                <p className="sp-msg err">CRM не подтверждает склад этой отгрузки. Сначала исправьте назначение поставщика; складской резерв автоматически не переносится.</p>
                            ) : null}
                            <div className="ow-fulfillment-issue-matrix">
                                <div className="ow-fulfillment-issue-matrix-head"><b>SKU</b><b>CRM</b><b>Посылка</b><b>Проводки</b></div>
                                {Array.from(new Set([
                                    ...Object.keys(item.crm_qty_by_sku || {}),
                                    ...Object.keys(item.shipment_qty_by_sku || {}),
                                    ...Object.keys(item.movement_qty_by_sku || {}),
                                ])).sort().map((sku) => (
                                    <div className="ow-fulfillment-issue-matrix-row" key={sku}>
                                        <span>{sku}</span>
                                        <span>{item.crm_qty_by_sku?.[sku] ?? "—"}</span>
                                        <span>{item.shipment_qty_by_sku?.[sku] ?? "—"}</span>
                                        <span>{item.movement_qty_by_sku?.[sku] ?? 0}</span>
                                    </div>
                                ))}
                            </div>
                            {item.stock_accounting_reason ? <p className="sp-msg err">{item.stock_accounting_reason}</p> : null}
                            {needsReturn(item) ? <p className="sp-hint">Товар заказа {item.salesdrive_order_id} уже списан: {Object.keys(item.written_off_qty_by_sku || {}).join(", ")}. После фактической приёмки оформите его через <a href={`/warehouse/returns?wh=${warehouse.id}`}>Возвраты</a>. Эта отмена не блокирует остальные свободные единицы. Закрытие проблемы не возвращает товар на остаток.</p> : null}
                            <details>
                                <summary>Данные сверки</summary>
                                <pre>{JSON.stringify(item.details || {}, null, 2)}</pre>
                            </details>
                            {item.issue_type === "manifest_confirmation_required" ? (
                                <p className="sp-hint">Строки из действующих резервов показаны только как предложение. Проверьте фактическое содержимое и подтвердите его с комментарием; событие-кандидат НП само по себе передачу не доказывает.</p>
                            ) : null}
                            {item.issue_type === "unpack_shortage" ? (
                                <p className="sp-msg err">После распаковки не хватает: {Object.entries(item.details?.missing_by_sku || {}).map(([sku, qty]) => `${sku} × ${qty}`).join(", ") || "—"}. SKU заблокированы до разбора. Найдите товар или оформите недостачу ручным списанием, затем закройте проблему с комментарием.</p>
                            ) : null}
                            {!readOnly(item) && !["crm_changed_after_handover", "unpack_shortage"].includes(item.issue_type) ? (
                                <label className="ow-fulfillment-comment">
                                    Действие
                                    <select value={actions[item.id] || defaultAction(item)}
                                        onChange={(event) => setActions((current) => ({ ...current, [item.id]: event.target.value }))}>
                                        {canUnpack(item) ? <option value="unpack">Распаковано: товар вернулся на полку</option> : null}
                                        <option value="confirm_manifest">Подтвердить фактический текущий состав</option>
                                        {item.issue_type !== "manifest_confirmation_required" ? <option value="repack">Пересобрать по указанному составу</option> : null}
                                        <option value="link_existing_operation">Связать с уже существующим списанием</option>
                                    </select>
                                </label>
                            ) : null}
                            {(actions[item.id] || defaultAction(item)) === "unpack" ? (
                                <div className="ow-fulfillment-issue-matrix">
                                    <div className="ow-fulfillment-issue-matrix-head"><b>SKU</b><b>В коробке</b><b>Вернулось на полку</b><b></b></div>
                                    {(unpackLines[item.id] || []).map((line, index) => (
                                        <div className="ow-fulfillment-issue-matrix-row" key={`${item.id}-unpack-${line.sku}`}>
                                            <span>{line.sku}</span>
                                            <span>{line.inBox}</span>
                                            <span>
                                                <input aria-label="Вернулось на полку" type="number" min="0" max={line.inBox} step="1" value={line.returned}
                                                    onChange={(event) => setUnpackLines((current) => ({
                                                        ...current, [item.id]: (current[item.id] || []).map((part, i) => i === index ? { ...part, returned: event.target.value } : part),
                                                    }))} />
                                            </span>
                                            <span>{Number(line.returned) < Number(line.inBox) ? `недостача ${Number(line.inBox) - Number(line.returned)}` : ""}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            {(actions[item.id] || defaultAction(item)) === "repack" ? (
                                <div className="ow-fulfillment-repack-lines">
                                    {(repackLines[item.id] || []).map((line, index) => (
                                        <div className="ow-fulfillment-repack-line" key={`${item.id}-${index}`}>
                                            <input aria-label="SKU" value={line.sku} onChange={(event) => setRepackLines((current) => ({
                                                ...current, [item.id]: (current[item.id] || []).map((part, i) => i === index ? { ...part, sku: event.target.value } : part),
                                            }))} />
                                            <input aria-label="Количество" type="number" min="1" step="1" value={line.qty} onChange={(event) => setRepackLines((current) => ({
                                                ...current, [item.id]: (current[item.id] || []).map((part, i) => i === index ? { ...part, qty: event.target.value } : part),
                                            }))} />
                                            <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => setRepackLines((current) => ({
                                                ...current, [item.id]: (current[item.id] || []).filter((_, i) => i !== index),
                                            }))}>Удалить</button>
                                        </div>
                                    ))}
                                    <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => setRepackLines((current) => ({
                                        ...current, [item.id]: [...(current[item.id] || []), { sku: "", qty: 1 }],
                                    }))}>Добавить SKU</button>
                                </div>
                            ) : null}
                            {!readOnly(item) ? <label className="ow-fulfillment-comment">
                                Комментарий оператора (обязательно)
                                <textarea
                                    rows={2}
                                    value={comments[item.id] || ""}
                                    onChange={(event) => setComments((current) => ({ ...current, [item.id]: event.target.value }))}
                                    placeholder="Что проверено, пересобрано или с какой проводкой связано: …"
                                />
                            </label> : null}
                            {READ_ONLY_TYPES.includes(item.issue_type) ? (
                                <p className="sp-hint">{item.issue_type === "handover_post_disabled"
                                    ? "Резерв сохранён. Списание будет повторено worker после включения механизма. Эта запись не блокирует публикацию SKU."
                                    : item.issue_type === "np_handover_code_unverified"
                                        ? "Код сохранён для сверки, но пока не считается доказательством передачи. Списание невозможно до проверки официального списка кодов и повторного ответа НП."
                                        : "Проверьте ТТН и дождитесь нового подтверждённого ответа НП. Само это событие списание не разрешает."}</p>
                            ) : null}
                            {!readOnly(item) ? <button
                                type="button"
                                className="sp-btn sp-btn-primary sp-btn-sm"
                                onClick={() => resolve(item)}
                                disabled={busyId === item.id || String(comments[item.id] || "").trim().length < 3}
                            >
                                {busyId === item.id ? "Проверяю…" : ({
                                    repack: "Пересобрать и сбросить сканирование",
                                    link_existing_operation: "Связать существующее списание",
                                    acknowledge_post_handover: "Зафиксировать расхождение CRM",
                                    acknowledge_shortage: "Закрыть недостачу (разобрано)",
                                    unpack: "Распаковано, снять резерв",
                                }[actions[item.id] || defaultAction(item)] || "Подтвердить фактический состав")}
                            </button> : null}
                        </article>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default OwnWarehouseFulfillmentIssuesTab;
